import React from "react";
import { Modal, Button } from "react-bootstrap";
import { formatMoney } from "../../../utils";

function titleCaseCategory(cat) {
  if (!cat) return "—";
  return String(cat)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatMetaKey(key) {
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatMetaValue(key, val) {
  if (val === null || val === undefined) return "—";
  if (typeof val === "object" && !Array.isArray(val)) {
    return (
      <pre className="mb-0 mt-1 text-xs bg-gray-50 p-2 rounded border border-gray-100 overflow-x-auto max-h-40">
        {JSON.stringify(val, null, 2)}
      </pre>
    );
  }
  if (Array.isArray(val)) {
    return (
      <pre className="mb-0 mt-1 text-xs bg-gray-50 p-2 rounded border border-gray-100 overflow-x-auto max-h-40">
        {JSON.stringify(val, null, 2)}
      </pre>
    );
  }
  const k = String(key).toLowerCase();
  const num = Number(val);
  if (
    Number.isFinite(num) &&
    (k.includes("amount") ||
      k.includes("ngn") ||
      k.includes("fee") ||
      k.includes("charged") ||
      k.includes("total"))
  ) {
    return formatMoney(num.toFixed(2));
  }
  if (typeof val === "boolean") return val ? "Yes" : "No";
  const s = String(val);
  if (k.includes("bearer")) {
    const low = s.toLowerCase();
    if (low === "member") return "Member";
    if (low === "organization" || low === "organisation") return "Cooperative (organization)";
  }
  return s;
}

function DetailLine({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 py-2 border-b border-gray-100 last:border-0">
      <span className="text-gray-500 text-sm shrink-0 min-w-[160px]">{label}</span>
      <div className="text-gray-900 text-sm font-medium text-right sm:text-right break-words max-w-full">
        {children}
      </div>
    </div>
  );
}

export default function PlatformLedgerDetailModal({ show, onHide, row, companyName, branchLabel }) {
  if (!row) return null;
  const meta = row.metadata && typeof row.metadata === "object" ? row.metadata : {};

  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable>
      <Modal.Header closeButton className="border-b border-gray-200">
        <Modal.Title className="text-[#383838] text-lg font-semibold">Ledger entry details</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3">
        <p className="text-gray-500 text-sm mb-3">
          Full platform ledger line as recorded for reconciliation (convenience revenue, Paystack costs,
          or transfer costs).
        </p>

        <DetailLine label="Entry ID">{row.id ?? "—"}</DetailLine>
        <DetailLine label="Recorded at">
          {row.created_at
            ? new Date(row.created_at).toLocaleString(undefined, {
                dateStyle: "medium",
                timeStyle: "short",
              })
            : "—"}
        </DetailLine>
        <DetailLine label="Cooperative">
          {companyName || (row.company_id != null ? `Company #${row.company_id}` : "—")}
          {row.company_id != null && (
            <span className="text-gray-400 font-normal text-xs ml-1">(ID: {row.company_id})</span>
          )}
        </DetailLine>
        <DetailLine label="Branch">{branchLabel ?? (row.branch_id != null ? `Branch #${row.branch_id}` : "—")}</DetailLine>
        <DetailLine label="Direction">
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold text-white ${
              String(row.direction).toLowerCase() === "credit" ? "bg-[#19bc31]" : "bg-[#ff4343]"
            }`}
          >
            {String(row.direction || "—").toUpperCase()}
          </span>
        </DetailLine>
        <DetailLine label="Category">{titleCaseCategory(row.category)}</DetailLine>
        <DetailLine label="Amount (NGN)">{formatMoney(Number(row.amount_ngn || 0).toFixed(2))}</DetailLine>
        <DetailLine label="Payment reference">
          <span className="font-mono text-xs break-all">{row.payment_reference || "—"}</span>
        </DetailLine>
        {(row.pending_payment_request_id ||
          row.payment_surcharge_line_id ||
          row.branch_withdrawal_request_id) && (
          <>
            {row.pending_payment_request_id != null && (
              <DetailLine label="Pending payment request ID">{row.pending_payment_request_id}</DetailLine>
            )}
            {row.payment_surcharge_line_id != null && (
              <DetailLine label="Payment surcharge line ID">{row.payment_surcharge_line_id}</DetailLine>
            )}
            {row.branch_withdrawal_request_id != null && (
              <DetailLine label="Branch withdrawal request ID">{row.branch_withdrawal_request_id}</DetailLine>
            )}
          </>
        )}

        <h6 className="text-[#383838] font-semibold mt-4 mb-2 text-sm">Metadata</h6>
        {Object.keys(meta).length === 0 ? (
          <p className="text-gray-500 text-sm mb-0">No metadata on this entry.</p>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-[#fafafa] p-3 space-y-2">
            {Object.entries(meta).map(([k, v]) => (
              <div key={k}>
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  {formatMetaKey(k)}
                </div>
                <div className="text-sm text-gray-900 mt-0.5">{formatMetaValue(k, v)}</div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="border-t border-gray-200">
        <Button
          variant="light"
          className="px-4 border border-gray-300 text-gray-800"
          onClick={onHide}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
